import { createContext, useContext, useEffect, useMemo, useState } from "react";

/* ============================================================
   Showing prices in a visitor's own currency.

   Every price in the database is Canadian dollars, and every order is charged
   in Canadian dollars. This file only changes what a price LOOKS like while
   someone is browsing — it is a courtesy, not a second price list.

   Because of that, two rules run through everything below:

     1. CAD is the truth. Any other currency is marked approximate, and the
        checkout always states the Canadian figure being charged.
     2. If rates are unavailable, the site shows Canadian dollars. It never
        guesses a rate and never shows a stale one it cannot date.

   Stripe shows the buyer their own currency on the payment page itself (its
   "Adaptive Pricing" setting), so the figure they finally approve is in their
   money, converted at Stripe's live rate rather than ours.
   ============================================================ */

export const CURRENCIES = [
  { code: "CAD", place: "Canada" },
  { code: "USD", place: "United States" },
  { code: "EUR", place: "Europe" },
  { code: "GBP", place: "United Kingdom" },
  { code: "AED", place: "United Arab Emirates" },
];

export const BASE = "CAD";

const STORE_KEY = "kg:currency";

/* Every price is formatted from ONE locale, Canadian English, rather than from
   each currency's home locale. That is what keeps the currencies apart on
   screen: en-CA writes Canadian money as "$520" and American money as
   "US$374", whereas en-US would write the same two amounts as "CA$520" and
   "$374". Formatting each currency in its own locale gave "$520" and "$374" —
   indistinguishable, which is exactly the confusion a currency switcher exists
   to prevent.

   Whole units only, matching how prices are written in the Studio. */
const LOCALE = "en-CA";
const formatter = (code) => new Intl.NumberFormat(LOCALE, {
  style: "currency", currency: code, maximumFractionDigits: 0, minimumFractionDigits: 0,
});

/* Built once per currency rather than on every price on the page. */
const cache = new Map();
export function formatIn(code, amount) {
  if (!cache.has(code)) cache.set(code, formatter(code));
  return cache.get(code).format(amount);
}

const CurrencyContext = createContext(null);

export function CurrencyProvider({ loadRates, children }) {
  const [code, setCodeState] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORE_KEY);
      return CURRENCIES.some((c) => c.code === saved) ? saved : BASE;
    } catch { return BASE; }
  });
  const [rates, setRates] = useState({});

  /* Asked for once, in the background. A visitor who never touches the switcher
     pays no attention cost for this, and one who does has the rates ready. */
  useEffect(() => {
    let alive = true;
    loadRates().then((r) => { if (alive) setRates((r && r.rates) || {}); });
    return () => { alive = false; };
  }, [loadRates]);

  const setCode = (next) => {
    setCodeState(next);
    try { window.localStorage.setItem(STORE_KEY, next); } catch { /* blocked or full */ }
  };

  const value = useMemo(() => {
    const rate = code === BASE ? 1 : rates[code];
    /* Chose a currency we have no rate for? Show Canadian dollars rather than a
       made-up number. The switcher reflects this by disabling that option. */
    const usable = Boolean(rate);
    const active = usable ? code : BASE;

    return {
      code: active,
      chosen: code,
      rates,
      /* True when what's on screen is a conversion rather than the real price. */
      approx: active !== BASE,
      available: (c) => c === BASE || Boolean(rates[c]),
      ready: Object.keys(rates).length > 0,
      setCode,
      /* A price for reading. */
      money: (cadAmount) => formatIn(active, active === BASE ? cadAmount : cadAmount * rate),
      /* The Canadian figure, for anywhere the exact amount charged matters. */
      base: (cadAmount) => formatIn(BASE, cadAmount),
    };
  }, [code, rates]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

/* Falls back to plain Canadian dollars outside a provider, so a component
   rendered on its own (a test, say) still shows a sensible price. */
const FALLBACK = {
  code: BASE, chosen: BASE, rates: {}, approx: false, ready: false,
  available: (c) => c === BASE, setCode: () => {},
  money: (n) => formatIn(BASE, n),
  base: (n) => formatIn(BASE, n),
};

export const useCurrency = () => useContext(CurrencyContext) || FALLBACK;

/* The common case: just formatting a price. */
export const useMoney = () => useCurrency().money;
